import type { CSSObject } from '@emotion/react'
import classNames from 'classnames'
import {
  useMultipleSelection,
  useSelect,
  UseSelectState,
  UseSelectStateChangeOptions,
  UseSelectStateChange,
} from 'downshift'
import uniqueId from 'lodash/uniqueId'
import React, {
  useMemo,
  useState,
  useRef,
  RefObject,
  useCallback,
  useEffect,
} from 'react'
import { type MenuOption as MenuOptionType } from '@opallabs/concepts'
import { Color, Theme } from '@opallabs/gem'
import {
  useDropdownPositioning,
  useMultiSelectDiff,
  useTheme,
} from '@opallabs/hooks'
import type { MultiSelectDiffObject } from '@opallabs/hooks'
import { Avatar } from '../Avatar'
import { ColorDotToken } from '../ColorToken'
import { Icon } from '../Icon'
import { MenuOption } from '../MenuOption'
import { Portal } from '../Portal'
import { TextInput, TextInputType } from '../TextInput'
import { useRadixDropdownBlurHelper } from '../utils/RadixDropdownBlurHelper'
import { SelectionList } from './SelectionList'
import * as styles from './styles'
import { TokenStrip } from './TokenStrip'

export {
  state as MultiCheckSelectStyleStates,
  selectors as MultiCheckSelectStyleSelectors,
} from './styles'

const PERSIST_PLACEHOLDER_ID = 'persist-placeholder'

/**
 * @note Use the onSelectionChange callback if you want to manage your own
 * selection state or make upstream changes after every add or remove event.
 * The onMenuCloseDiff callback provides an object that describes what items
 * have been added or removed from the original selection when the menu closes
 * after a set of changes.
 *
 * The initialSelection prop should represent state after any data has been
 * recieved from the server. Downshift manages its own state, and won't update
 * the intial selection after the component has mounted via
 * state management/useEffect.
 */
export interface MultiCheckSelectProps<T extends MenuOptionType> {
  asUserSelect?: boolean
  /**
   * Render function that overrides the default display for selected items.
   */
  children?: (injected: { selectedItems: T[] }) => React.ReactNode
  className?: string
  colorsEnabled?: boolean
  /**
   * Rendered inside the open menu, below the option list. A render function
   * rather than a node because a footer action usually needs the live
   * selection, which Downshift owns and no parent can otherwise read while the
   * menu is open. `focusSearch` hands focus back to the search input, which is
   * where Escape closes the menu: use it when a dialog the footer opened closes.
   */
  customFooter?: (injected: {
    focusSearch: () => void
    selectedItems: T[]
  }) => React.ReactNode
  disabled?: boolean
  disableInitialCheckedItems?: boolean
  externalRef?: RefObject<HTMLButtonElement>
  hasError?: boolean
  htmlId?: string
  initialSelection?: Array<T>
  inputWidth?: number | string
  isSearchable?: boolean
  label?: string
  menuWidth?: number | string
  onOpenChange?: (open: boolean) => void
  onMenuCloseDiff?: (diff: MultiSelectDiffObject<T>) => void
  onPersistentOptionSelect?: () => void
  onSelectionChange?: (changedOption: T, type: 'add' | 'remove') => void
  options: Array<T>
  pendingValues?: ReadonlySet<string>
  persistentOption?: React.ReactNode
  placeholder?: string
  showTriggerIcon?: boolean
  sortSelectionList?: boolean
  sortSelectedToTop?: boolean
  styleOverrides?: CSSObject
  tabIndex?: number
  theme?: Theme
  trailingNode?: React.ReactNode
}

export const MultiCheckSelect = <T extends MenuOptionType = MenuOptionType>({
  asUserSelect = false,
  children,
  className,
  colorsEnabled = false,
  customFooter,
  disabled = false,
  disableInitialCheckedItems = false,
  externalRef,
  hasError = false,
  htmlId,
  initialSelection = [],
  inputWidth = '100%',
  isSearchable = true,
  label,
  menuWidth,
  onOpenChange,
  onMenuCloseDiff,
  onPersistentOptionSelect,
  onSelectionChange,
  options,
  pendingValues,
  persistentOption,
  placeholder = 'Select...',
  showTriggerIcon = true,
  sortSelectionList,
  sortSelectedToTop = true,
  styleOverrides,
  tabIndex,
  theme: propTheme,
  trailingNode,
}: MultiCheckSelectProps<T>) => {
  const providedTheme = useTheme()
  const theme = propTheme ?? providedTheme
  const hasTokenStrip = asUserSelect && isSearchable

  const [searchTerm, setSearchTerm] = useState('')
  const [searchFocused, setSearchFocused] = useState(false)
  const [componentId] = useState(uniqueId('lapidary-multi-select-'))

  const searchRef = useRef<HTMLInputElement>(null)
  const footerRef = useRef<HTMLDivElement>(null)
  const [menuContainerRef, setMenuContainerRef] = useState<
    RefObject<HTMLDivElement>
  >({ current: null })
  const buttonRef = useRef<HTMLButtonElement>(null)
  const toggleRef = externalRef ?? buttonRef

  const { onSelect, selectedItems, getMultiSelectDiff } = useMultiSelectDiff({
    initialSelection,
    key: 'value',
  })
  const { getDropdownProps } = useMultipleSelection<T>({
    itemToKey: (item) => item?.value,
    initialSelectedItems: initialSelection,
    selectedItems,
  })
  const selectedValues = useMemo(
    () => selectedItems.map((item) => item.value),
    [selectedItems]
  )

  const stateReducer = (
    state: UseSelectState<T>,
    actionAndChanges: UseSelectStateChangeOptions<T>
  ): Partial<UseSelectState<T>> => {
    const { changes, type } = actionAndChanges
    let newSelectedItem = changes.selectedItem

    switch (type) {
      case useSelect.stateChangeTypes.ToggleButtonKeyDownArrowDown:
        // Opening with ArrowDown starts at the first highlighted item
        if (!state.isOpen) {
          return {
            ...changes,
            highlightedIndex: 0,
          }
        }
        return changes
      case useSelect.stateChangeTypes.ToggleButtonKeyDownArrowUp:
        return {
          ...changes,
          highlightedIndex:
            // Force state change if pressing Up on first item, so that
            // the `onStateChange` handler can focus the search input
            isSearchable &&
            state.highlightedIndex === 0 &&
            changes.highlightedIndex === 0
              ? -1
              : changes.highlightedIndex,
        }
      case useSelect.stateChangeTypes.ToggleButtonKeyDownSpaceButton:
      case useSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
      case useSelect.stateChangeTypes.ItemClick:
        if (changes.selectedItem?.value === PERSIST_PLACEHOLDER_ID) {
          onPersistentOptionSelect?.()
          // Do not allow selecting persistent option, clear selected item
          newSelectedItem = null
        }

        return {
          ...changes,
          isOpen: true, // keep the menu open after selection.
          // User mode hands focus straight back to the search input, and a
          // stale index would break `onSearchBlur`'s close check.
          highlightedIndex: hasTokenStrip ? -1 : state.highlightedIndex,
          selectedItem: newSelectedItem,
        }
      case useSelect.stateChangeTypes.ToggleButtonBlur: {
        // Downshift's outside-click tracker only knows the option list and the
        // toggle button, so focus landing on a footer control reads as a blur
        // to it and would close the menu before that control's click lands.
        const isFocusWithinFooter = Boolean(
          footerRef.current?.contains(document.activeElement)
        )

        return {
          ...changes,
          // keep the menu open if focusing search
          isOpen: isSearchable
            ? searchFocused || isFocusWithinFooter
            : changes.isOpen || isFocusWithinFooter,
          highlightedIndex:
            isSearchable && searchFocused ? -1 : changes.highlightedIndex,
        }
      }
    }

    return changes
  }

  const onStateChange = ({
    type,
    highlightedIndex,
    selectedItem: newSelectedItem,
  }: UseSelectStateChange<T>) => {
    switch (type) {
      case useSelect.stateChangeTypes.ToggleButtonKeyDownArrowDown:
      case useSelect.stateChangeTypes.ToggleButtonKeyDownArrowUp:
        // If pressing Up on first menu option and `isSearchable = true`,
        // focus the search input
        if (highlightedIndex === -1 && isSearchable) {
          focusSearch()
        }
        break
      // Selection on blur is undesirable for a multi-select where
      // selected options remain in the menu, so we won't trigger
      // on ToggleButtonBlur
      case useSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
      case useSelect.stateChangeTypes.ToggleButtonKeyDownSpaceButton:
      case useSelect.stateChangeTypes.ItemClick:
        if (newSelectedItem) {
          onSelectedItemChange({ type, selectedItem: newSelectedItem })
          if (hasTokenStrip) {
            setSearchTerm('')
            focusSearch()
          }
        }
    }
  }

  const onIsOpenChange = ({ isOpen = false }: UseSelectStateChange<T>) => {
    onOpenChange?.(isOpen)

    if (isOpen) {
      focusSearch()
    } else {
      if (searchTerm !== '') {
        setSearchTerm('')
      }

      if (onMenuCloseDiff) {
        return onMenuCloseDiff(getMultiSelectDiff())
      }
    }
  }

  const changeSelection = (item: T) => {
    const verb = onSelect(item)
    onSelectionChange?.(item, verb)
  }

  // called on item click and enter keypress (from state reducer)
  const onSelectedItemChange = ({ selectedItem }: UseSelectStateChange<T>) => {
    if (!selectedItem) {
      return
    }

    changeSelection(selectedItem)
  }

  const baseFilteredOptions = useMemo(() => {
    const persistentOptionPlaceholder = {
      value: PERSIST_PLACEHOLDER_ID,
      label: 'Persistent Option',
    } as T

    const searchMatches = searchTerm
      ? options.filter((option) =>
          option.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : options
    const tokenStripMatches = searchMatches.filter(
      (option) => !selectedValues.includes(option.value)
    )
    const baseOptions = hasTokenStrip ? tokenStripMatches : searchMatches

    // Add persistent option if provided
    if (persistentOption) {
      return [...baseOptions, persistentOptionPlaceholder]
    }

    return baseOptions
  }, [hasTokenStrip, options, persistentOption, searchTerm, selectedValues])

  const [internalIsOpen, setInternalIsOpen] = useState(false)
  const [selectionForSort, setSelectionForSort] = useState<T[]>([])

  const filteredOptions = useMemo(() => {
    // Sort selected items to top if enabled and menu is open
    if (sortSelectedToTop && internalIsOpen) {
      const persistentOption = baseFilteredOptions.find(
        (option) => option.value === PERSIST_PLACEHOLDER_ID
      )
      const regularOptions = baseFilteredOptions.filter(
        (option) => option.value !== PERSIST_PLACEHOLDER_ID
      )

      const selectedOptions = selectionForSort.filter((item) =>
        regularOptions.some((option) => option.value === item.value)
      )
      const selectedOptionValues = selectionForSort.map((item) => item.value)
      const unselectedOptions = regularOptions.filter(
        (option) => !selectedOptionValues.includes(option.value)
      )

      const sortedOptions = [...selectedOptions, ...unselectedOptions]
      return persistentOption
        ? [...sortedOptions, persistentOption]
        : sortedOptions
    }

    return baseFilteredOptions
  }, [baseFilteredOptions, sortSelectedToTop, internalIsOpen, selectionForSort])

  const {
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    setHighlightedIndex,
    getItemProps,
    closeMenu,
  } = useSelect<T>({
    stateReducer,
    onStateChange,
    onIsOpenChange,
    selectedItem: null,
    id: componentId,
    items: filteredOptions,
    itemToString: (item) => (item ? item.label : ''),
    isItemDisabled: (option) =>
      disableInitialCheckedItems && initialSelection.includes(option),
  })

  // Track when menu opens to sort selected items to top
  useEffect(() => {
    setInternalIsOpen(isOpen)
    if (isOpen) {
      setSelectionForSort(selectedItems)
    }
  }, [isOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  const isSelected = (option: T) =>
    selectedValues.includes(option.value) || selectedItems.includes(option)

  const focusSearch = () => {
    if (isSearchable) {
      setSearchFocused(true)
      setTimeout(() => searchRef.current?.focus(), 0)
    }
  }

  // A click back into the search input after a footer control took focus
  // fires `ToggleButtonBlur`, which closes the menu unless this is set.
  const onSearchFocus = () => setSearchFocused(true)

  const onSearchBlur = (e: React.FocusEvent) => {
    setSearchFocused(false)

    // A footer control lives inside the menu container, so focus moving to it
    // is not an outside click. Without this the menu closes before a footer
    // button's click event lands.
    const isFocusMovedIntoMenu =
      e.relatedTarget instanceof Node &&
      Boolean(menuContainerRef.current?.contains(e.relatedTarget))

    // If search loses focus, and search is still highlighted (index -1),
    // then focus was not given to the menu. Ergo, some outside element
    // gained focus, and we should close the menu
    // Ignore if relatedTarget is the toggle button, this happens when
    // you use ArrowDown to navigate to the menu items
    if (
      highlightedIndex === -1 &&
      e.relatedTarget !== toggleRef.current &&
      !isFocusMovedIntoMenu
    ) {
      closeMenu()
    }
  }

  const onSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      // The Escape is spent closing the menu; a containing panel or dialog
      // must not also close.
      e.stopPropagation()
      setTimeout(() => closeMenu(), 0)
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (filteredOptions.length === 0) return
      toggleRef.current?.focus()
      setHighlightedIndex(0)
    }
  }

  const handleMenuContainerRefChange = useCallback(
    (node: HTMLDivElement | null) => setMenuContainerRef({ current: node }),
    [setMenuContainerRef]
  )

  // Workaround to fix outside click interactions with OptionCell dropdown
  useRadixDropdownBlurHelper(isOpen, [menuContainerRef, toggleRef], () =>
    closeMenu()
  )

  // Apply smart positioning to dropdown menu
  const { isPositioned } = useDropdownPositioning(
    toggleRef.current,
    menuContainerRef.current,
    { minContentWidth: asUserSelect ? styles.multiUserMenuMinWidth : undefined }
  )

  return (
    <div
      css={[styles.inputWrapper, styleOverrides]}
      className={classNames('lapidary-multi-select', className, {
        [styles.state.hasSelection]: Boolean(selectedItems.length),
      })}
    >
      {label && (
        <label css={styles.inputLabel(theme)} {...getLabelProps()}>
          {label}
        </label>
      )}
      <button
        type="button"
        className={classNames(
          styles.selectors.input,
          // Legacy; retained for backwards compatibility
          'multi-select-value-input'
        )}
        style={{ width: inputWidth }}
        css={styles.inputContainer({ theme, isOpen, hasError })}
        {...getToggleButtonProps({
          ...getDropdownProps({
            preventKeyAction: isOpen,
            disabled,
            tabIndex,
            id: htmlId,
            ref: toggleRef,
            // Manually set aria-activedescendant to search input when focused
            // Don't set otherwise, so Downshift can handle the list items
            ...(searchFocused
              ? { 'aria-activedescendant': `${componentId}-search` }
              : {}),
            ...(label === undefined && {
              'aria-label': 'Open multi-select menu',
            }),
          }),
          // `getDropdownProps` drops `onKeyDown` while the menu is open, so it
          // goes here. Downshift closes the menu on this Escape. A container
          // that waits for Escape to bubble (Block details panel) must not
          // also close.
          ...(isOpen && {
            onKeyDown: (e: React.KeyboardEvent) => {
              if (e.key === 'Escape') e.stopPropagation()
            },
          }),
        })}
      >
        <span css={styles.selection(theme)}>
          {children ? (
            children({ selectedItems })
          ) : (
            <>
              <SelectionList
                options={options}
                items={selectedItems}
                sortSelectionList={sortSelectionList}
                colorsEnabled={colorsEnabled}
              />
              {!selectedItems.length && (
                <span className={styles.selectors.inputPlaceholder}>
                  {placeholder}
                </span>
              )}
            </>
          )}
        </span>
        {trailingNode}
        {showTriggerIcon && (
          <Icon
            glyph="gem-chevron-s"
            className="multi-select-toggle"
            ariaHidden
            color={Color[theme].Text.Default}
          />
        )}
      </button>
      <Portal>
        <div
          style={{
            width: menuWidth,
            display: !isOpen ? 'none' : undefined,
            visibility: !isPositioned ? 'hidden' : undefined,
          }}
          id={`${componentId}-menu-container`}
          ref={handleMenuContainerRefChange}
          css={styles.menu({
            hasTokenStrip,
            theme,
            optionCount: filteredOptions.length,
          })}
        >
          {isOpen && hasTokenStrip && (
            <TokenStrip
              componentId={componentId}
              onRemove={(item) => {
                changeSelection(item)
                focusSearch()
              }}
              onSearchBlur={onSearchBlur}
              onSearchFocus={onSearchFocus}
              onSearchChange={setSearchTerm}
              onSearchKeyDown={onSearchKeyDown}
              pendingValues={pendingValues}
              searchRef={searchRef}
              searchTerm={searchTerm}
              selectedItems={selectedItems}
            />
          )}
          {isOpen && isSearchable && !hasTokenStrip && (
            <div css={styles.searchContainer({ theme })}>
              <TextInput
                ref={searchRef}
                htmlId={`${componentId}-search`}
                controlsHtmlId={`${componentId}-menu`}
                type={TextInputType.Search}
                additionalAriaLabels={{ 'aria-label': 'Search options list' }}
                clearable={true}
                leadingIconGlyph="gem-search"
                onChange={(val: string) => setSearchTerm(val)}
                onInputBlur={onSearchBlur}
                onInputFocus={onSearchFocus}
                onKeyDown={onSearchKeyDown}
                placeholder=""
                theme={theme}
              />
            </div>
          )}
          <ul css={styles.menuContent} {...getMenuProps()}>
            {isOpen &&
              filteredOptions.map((option, index) => {
                if (option.value !== PERSIST_PLACEHOLDER_ID) {
                  const selected = isSelected(option)
                  return (
                    <MenuOption.Root
                      key={option.value}
                      id={option.value}
                      optionText={option.label}
                      selected={selected}
                      disabled={
                        (disableInitialCheckedItems &&
                          initialSelection.includes(option)) ||
                        option.disabled
                      }
                    >
                      <MenuOption.Body
                        highlightedByKeyboard={highlightedIndex === index}
                        {...getItemProps({
                          index,
                          item: option,
                          'aria-selected': selected,
                        })}
                      >
                        {!hasTokenStrip && <MenuOption.Checkbox />}
                        {colorsEnabled && option.color && (
                          <ColorDotToken color={option.color} />
                        )}
                        {asUserSelect ? (
                          <Avatar
                            fullName={option.label}
                            src={option.avatarUrl}
                          />
                        ) : null}
                        <MenuOption.OptionText />
                        {option.trailingNode}
                      </MenuOption.Body>
                    </MenuOption.Root>
                  )
                }
                return (
                  <li
                    key={option.value}
                    className={styles.selectors.persistentOption}
                    css={styles.persistentOption(
                      theme,
                      highlightedIndex === index
                    )}
                    {...getItemProps({
                      item: option,
                      index,
                    })}
                  >
                    {persistentOption}
                  </li>
                )
              })}
            {isOpen && !filteredOptions.length && !persistentOption ? (
              <li key="no-options" css={styles.noOptions}>
                No options available
              </li>
            ) : null}
          </ul>
          {isOpen && customFooter && (
            <div
              ref={footerRef}
              role="presentation"
              // Stops a pointer click from moving focus at all, which is what
              // makes the footer work in browsers that do not focus a button
              // when it is clicked. The focus guards in `stateReducer` and
              // `onSearchBlur` cover the keyboard path instead.
              onMouseDown={(e) => e.preventDefault()}
              css={styles.customFooter(theme)}
            >
              {customFooter({ focusSearch, selectedItems })}
            </div>
          )}
        </div>
      </Portal>
    </div>
  )
}
MultiCheckSelect.displayName = 'Lapidary: MultiCheckSelect'
